import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { alpha, useTheme } from "@mui/material/styles";
import { fCurrency } from "../../utils/formatNumber";

type TrustMoneyBucket = {
  count: number;
  totalAmount: number;
  items: unknown[];
};

type TrustMoneyPieChartResponse = {
  checksDateRange: {
    from: string;
    to: string;
  };
  deposits: TrustMoneyBucket;
  escrowLedgers: TrustMoneyBucket;
  recordingLedgers: TrustMoneyBucket;
  outstandingChecks: TrustMoneyBucket;
  deadChecks: TrustMoneyBucket;
  nonZeroLedgers: TrustMoneyBucket;
};

type PieSlice = {
  id: number;
  label: string;
  value: number;
  href: string;
  color: string;
  start: number;
  end: number;
  mid: number;
  path: string;
  percentLabelX: number;
  percentLabelY: number;
  percent: number;
};

const polarToCartesian = (cx: number, cy: number, radius: number, angleDeg: number) => {
  const angleRad = (angleDeg * Math.PI) / 180;
  return { x: cx + radius * Math.cos(angleRad), y: cy + radius * Math.sin(angleRad) };
};

const donutArcPath = (
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number
) => {
  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerRadius, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerEnd.x} ${innerEnd.y}`,
    "Z"
  ].join(" ");
};

export default function TrustMoneyPieChart() {
  const theme = useTheme();
  const chartWidth = 980;
  const chartHeight = 560;
  const centerX = 490;
  const centerY = 280;
  const innerRadius = 98;
  const outerRadius = 162;
  const today = dayjs();
  const defaultChecksTo = today.subtract(10, "days");
  const defaultChecksFrom = dayjs("7/4/2023");
  const [data, setData] = useState<TrustMoneyPieChartResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data: response } = await axios.get<TrustMoneyPieChartResponse>(
          `/api/TrustMoneyPieChart/GetData?from=${defaultChecksFrom}&to=${defaultChecksTo}`
        );
        setData(response);
      } catch (e: any) {
        setError(e?.message || "Failed to load trust money data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const pieData = useMemo(() => {
    if (!data) return [];

    const nonZeroItems = (data.nonZeroLedgers.items || []) as any[];
    const getAmount = (item: any) => {
      const value = item?.amount ?? item?.Amount ?? 0;
      return typeof value === "number" ? value : Number(value) || 0;
    };
    const getDepartment = (item: any) => {
      return String(item?.orderInfo?.department ?? item?.OrderInfo?.Department ?? "").toUpperCase();
    };

    const nonZeroNyTotal = nonZeroItems
      .filter((item) => getDepartment(item) === "NY")
      .reduce((sum, item) => sum + getAmount(item), 0);
    const nonZeroOtherTotal = Math.max(0, data.nonZeroLedgers.totalAmount - nonZeroNyTotal);

    return [
      { id: 0, label: "Deposits", value: data.deposits.totalAmount, href: "/deposits", color: "#0ea5e9" },
      { id: 1, label: "Escrow Ledgers", value: data.escrowLedgers.totalAmount, href: "/escrow-ledgers", color: "#10b981" },
      { id: 2, label: "Recording Ledgers", value: data.recordingLedgers.totalAmount, href: "/recording-ledgers", color: "#f59e0b" },
      { id: 3, label: "Outstanding Checks", value: data.outstandingChecks.totalAmount, href: "/checks", color: "#8b5cf6" },
      { id: 6, label: "Dead Checks", value: data.deadChecks.totalAmount,
        href: `/checks/dead?from=${dayjs(data.checksDateRange.from).format("YYYY-MM-DD")}&to=${dayjs(data.checksDateRange.to).format("YYYY-MM-DD")}`, color: "#64748b" },
      { id: 4, label: "New York", value: nonZeroNyTotal, href: "/non-zero-ledger-balances-ny", color: "#ec4899" },
      { id: 5, label: "Non Zero Ledgers", value: nonZeroOtherTotal, href: "/non-zero-ledger-balances", color: "#ef4444" }
    ].filter((slice) => slice.value > 0);
  }, [data]);
  const total = useMemo(() => pieData.reduce((sum, item) => sum + item.value, 0), [pieData]);
  const slices = useMemo(() => {
    if (!total) return [] as PieSlice[];

    const paddingAngle = 1.2;
    const minimumVisibleSpan = 0.9;
    const startAngle = -90;
    let running = 0;

    return pieData.map((slice) => {
      const degrees = (slice.value / total) * 360;
      const rawStart = startAngle + running;
      const rawEnd = rawStart + degrees;
      const safePadding = Math.min(paddingAngle, Math.max(0, degrees - minimumVisibleSpan));
      const start = rawStart + safePadding / 2;
      const end = rawEnd - safePadding / 2;
      const mid = (start + end) / 2;
      running += degrees;

      const labelRadius = (innerRadius + outerRadius) / 2;
      const percentPos = polarToCartesian(centerX, centerY, labelRadius, mid);

      return {
        ...slice,
        start,
        end,
        mid,
        path: donutArcPath(centerX, centerY, innerRadius, outerRadius, start, end),
        percentLabelX: percentPos.x,
        percentLabelY: percentPos.y,
        percent: total ? (slice.value / total) * 100 : 0
      };
    });
  }, [pieData, total]);

  const callouts = useMemo(() => {
    if (!total) return [];

    const leftBoxX = 20;
    const rightBoxX = chartWidth - 20 - 260;
    const boxWidth = 260;
    const minY = 64;
    const maxY = chartHeight - 64;
    const minGap = 88;
    const elbowOffset = 74;
    const rawCallouts = slices.map((slice) => {
      const radians = (slice.mid * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);
      const isRight = cos >= 0;

      const x1 = centerX + cos * outerRadius;
      const y1 = centerY + sin * outerRadius;
      const x2 = centerX + cos * (outerRadius + 18);
      const y2 = centerY + sin * (outerRadius + 18);

      return {
        ...slice,
        isRight,
        x1,
        y1,
        x2,
        y2,
        labelY: y2,
        leftBoxX,
        rightBoxX,
        boxWidth,
        elbowOffset
      };
    });

    const distribute = (items: typeof rawCallouts) => {
      if (!items.length) return items;

      const sorted = [...items].sort((a, b) => a.labelY - b.labelY);
      for (let i = 0; i < sorted.length; i += 1) {
        if (i === 0) {
          sorted[i].labelY = Math.max(minY, sorted[i].labelY);
        } else {
          sorted[i].labelY = Math.max(sorted[i].labelY, sorted[i - 1].labelY + minGap);
        }
      }

      if (sorted[sorted.length - 1].labelY > maxY) {
        const shift = sorted[sorted.length - 1].labelY - maxY;
        for (let i = 0; i < sorted.length; i += 1) {
          sorted[i].labelY -= shift;
        }
      }

      for (let i = sorted.length - 2; i >= 0; i -= 1) {
        if (sorted[i + 1].labelY - sorted[i].labelY < minGap) {
          sorted[i].labelY = sorted[i + 1].labelY - minGap;
        }
      }

      sorted[0].labelY = Math.max(minY, sorted[0].labelY);
      return sorted;
    };

    const left = distribute(rawCallouts.filter((x) => !x.isRight)).map((x) => ({
      ...x,
      x3: centerX - outerRadius - x.elbowOffset,
      labelX: x.leftBoxX,
      textAnchor: "end" as const
    }));
    const right = distribute(rawCallouts.filter((x) => x.isRight)).map((x) => ({
      ...x,
      x3: centerX + outerRadius + x.elbowOffset,
      labelX: x.rightBoxX,
      textAnchor: "start" as const
    }));

    return [...left, ...right];
  }, [slices, total, centerX, outerRadius, chartWidth, chartHeight]);

  return (
    <>
      <Helmet>
        <title>Trust Money Pie Chart | TitleQ</title>
      </Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack spacing={3}>
          <Card
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              background: `linear-gradient(135deg, ${alpha("#0f172a", 0.96)} 0%, ${alpha("#1d4ed8", 0.9)} 100%)`,
              color: "common.white",
              position: "relative"
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -110,
                right: -80,
                width: 260,
                height: 260,
                borderRadius: "50%",
                background: `radial-gradient(circle at center, ${alpha("#67e8f9", 0.4)} 0%, transparent 70%)`
              }}
            />
            <CardContent sx={{ position: "relative", zIndex: 1 }}>
              <Stack spacing={1}>
                <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
                  Trust Money Distribution
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Combined totals across deposits, escrow, recording, checks, and non-zero ledgers.
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    label={`Total: ${fCurrency(total)}`}
                    sx={{
                      mt: 1,
                      bgcolor: alpha("#ffffff", 0.14),
                      color: "common.white",
                      fontWeight: 700
                    }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!!error && <Alert severity="error">{error}</Alert>}

          {!!data && (
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ position: "relative", width: "100%", maxWidth: 980, mx: "auto", minHeight: 560 }}>
                  <Box
                    component="svg"
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  >
                    {slices.map((slice) => (
                      <path
                        key={`slice-${slice.id}`}
                        d={slice.path}
                        fill={slice.color}
                        stroke={theme.palette.background.paper}
                        strokeWidth={1.25}
                      />
                    ))}
                    {slices.map((slice) =>
                      slice.percent >= 4 ? (
                        <text
                          key={`percent-${slice.id}`}
                          x={slice.percentLabelX}
                          y={slice.percentLabelY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{ fill: theme.palette.common.white, fontSize: 13, fontWeight: 700 }}
                        >
                          {`${Math.round(slice.percent)}%`}
                        </text>
                      ) : null
                    )}
                    {callouts.map((slice) => (
                      <g key={`callout-${slice.id}`}>
                        <line x1={slice.x1} y1={slice.y1} x2={slice.x2} y2={slice.y2} stroke={slice.color} strokeWidth={1.7} />
                        <line x1={slice.x2} y1={slice.y2} x2={slice.x3} y2={slice.labelY} stroke={slice.color} strokeWidth={1.7} />
                        <line
                          x1={slice.x3}
                          y1={slice.labelY}
                          x2={slice.textAnchor === "start" ? slice.labelX : slice.labelX + 260}
                          y2={slice.labelY}
                          stroke={slice.color}
                          strokeWidth={1.7}
                        />
                        <a href={slice.href} target="_blank" rel="noopener noreferrer">
                          <rect
                            x={slice.labelX}
                            y={slice.labelY - 20}
                            width={260}
                            height={76}
                            rx={8}
                            fill={alpha(theme.palette.background.paper, 0.96)}
                            stroke={alpha(slice.color, 0.42)}
                            style={{
                              filter: `drop-shadow(0 3px 10px ${alpha(slice.color, 0.15)})`
                            }}
                          />
                          <circle cx={slice.labelX + 18} cy={slice.labelY + 5} r={5.4} fill={slice.color} />
                          <text
                            x={slice.labelX + 34}
                            y={slice.labelY + 12}
                            textAnchor="start"
                            style={{ fill: theme.palette.text.primary, fontSize: 12, fontWeight: 700 }}
                          >
                            <tspan>{slice.label}</tspan>
                            <tspan
                              dx="6"
                              style={{
                                fill: alpha(theme.palette.text.primary, 0.58),
                                fontSize: 10,
                                fontWeight: 600
                              }}
                            >
                              {`${Math.round(slice.percent)}%`}
                            </tspan>
                          </text>
                          <text
                            x={slice.labelX + 18}
                            y={slice.labelY + 48}
                            textAnchor="start"
                            style={{ fill: theme.palette.text.primary, fontSize: 16, fontWeight: 700 }}
                          >
                            {fCurrency(slice.value)}
                          </text>
                        </a>
                      </g>
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </>
  );
}
